<script setup lang="ts">
import type { ThemeSettings, TwitchEmbedSettings } from '~/stores/auth'
import { DEFAULT_TWITCH_EMBED_SETTINGS } from '~/stores/auth'
import { hexToRgb } from '~/composables/useLayoutStyles'
import { useCardHover } from '~/composables/useCardHover'

interface TwitchClip {
  id: string
  title: string
  url: string
  embedUrl: string
  thumbnailUrl: string
  viewCount: number
  duration: number
  createdAt: string
}

interface TwitchScheduleSegment {
  id: string
  startTime: string
  endTime: string
  title: string
  category: string | null
  isRecurring: boolean
}

interface TwitchProfileData {
  twitchUserId: string
  twitchLogin: string
  displayName: string
  avatarUrl: string
  broadcasterType: string
  description: string
  offlineImageUrl: string
  gameName: string | null
  title: string | null
  tags: string[]
  isLive: boolean
  viewerCount: number
  streamThumbnailUrl: string | null
  startedAt: string | null
  followerCount: number
  subscriberCount: number | null
  schedule: TwitchScheduleSegment[]
  clips: TwitchClip[]
  channelUrl: string
}

const props = defineProps<{
  characterId: string
  themeSettings: ThemeSettings
  twitchSettings?: TwitchEmbedSettings
  cardStyle?: Record<string, string>
  editMode?: boolean
}>()

const emit = defineEmits<{
  'open-twitch-settings': []
}>()

const skinPreview = inject<boolean>('skinPreview', false)
const { onCardMouseMove, onCardMouseLeave } = useCardHover(toRef(props, 'themeSettings'))

const tw = computed<TwitchEmbedSettings>(() => ({
  ...DEFAULT_TWITCH_EMBED_SETTINGS,
  ...(props.twitchSettings ?? {})
}))

const profileData = ref<TwitchProfileData | null>(null)
const loaded = ref(false)

async function fetchProfileData() {
  const params: Record<string, string> = {}
  if (tw.value.showClips) {
    params.clipCount = String(tw.value.clipCount)
  }
  if (tw.value.showSchedule) {
    params.includeSchedule = 'true'
  }
  profileData.value = await $fetch<TwitchProfileData>(`/api/twitch/profile/${props.characterId}`, { params })
}

onMounted(async () => {
  if (skinPreview) {
    loaded.value = true
    return
  }
  try {
    await fetchProfileData()
  } catch {
    // Silent fail - component won't render
  } finally {
    loaded.value = true
  }
})

// Re-fetch when settings change (e.g. after saving embed settings)
watch(() => props.twitchSettings, async () => {
  if (!loaded.value) return
  try {
    await fetchProfileData()
  } catch {
    // Silent fail
  }
}, { deep: true })

const cardBg = computed(() => {
  const rgb = hexToRgb(props.themeSettings.cardColor)
  return `rgba(${rgb.join(',')}, ${Math.min(props.themeSettings.cardOpacity / 100, 0.6)})`
})

// Clips grid vs carousel
const visibleClips = computed(() => {
  if (!tw.value.showClips) return []
  return profileData.value?.clips ?? []
})
const useCarousel = computed(() => visibleClips.value.length > 3)

// Carousel state
const activeIndex = ref(0)
const carouselItems = computed(() =>
  visibleClips.value.map(c => ({ ...c, class: 'basis-1/3' }))
)
function onClickPrev() {
  activeIndex.value--
}
function onClickNext() {
  activeIndex.value++
}
function onSelect(index: number) {
  activeIndex.value = index
}

function formatCount(count: number): string {
  if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`
  if (count >= 1_000) return `${(count / 1_000).toFixed(1).replace(/\.0$/, '')}K`
  return count.toLocaleString()
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

const streamUptime = computed(() => {
  if (!profileData.value?.isLive || !profileData.value.startedAt) return null
  const ms = Date.now() - new Date(profileData.value.startedAt).getTime()
  const hours = Math.floor(ms / 3_600_000)
  const minutes = Math.floor((ms % 3_600_000) / 60_000)
  if (hours > 0) return `${hours}h ${minutes}m`
  return `${minutes}m`
})

function formatScheduleTime(iso: string): string {
  const d = new Date(iso)
  const now = new Date()
  const tomorrow = new Date(now)
  tomorrow.setDate(tomorrow.getDate() + 1)

  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })

  if (d.toDateString() === now.toDateString()) return `Today ${time}`
  if (d.toDateString() === tomorrow.toDateString()) return `Tomorrow ${time}`
  return `${d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })} ${time}`
}

const broadcasterBadge = computed(() => {
  if (!profileData.value) return null
  if (profileData.value.broadcasterType === 'partner') return 'Partner'
  if (profileData.value.broadcasterType === 'affiliate') return 'Affiliate'
  return null
})
</script>

<template>
  <SkinPreviewPlaceholder
    v-if="skinPreview"
    label="Twitch Embed"
    icon="i-simple-icons-twitch"
    :card-style="cardStyle"
    :theme-settings="themeSettings"
  />
  <div
    v-else-if="loaded && profileData"
    class="group rounded-xl overflow-hidden"
    :style="{
      background: cardBg,
      ...cardStyle
    }"
    @mousemove="onCardMouseMove"
    @mouseleave="onCardMouseLeave"
  >
    <!-- Live stream thumbnail or offline image -->
    <div
      v-if="profileData.isLive && profileData.streamThumbnailUrl && tw.showLiveStatus"
      class="w-full aspect-video overflow-hidden relative"
    >
      <img
        :src="profileData.streamThumbnailUrl"
        :alt="`${profileData.displayName} live stream`"
        class="w-full h-full object-cover"
        loading="lazy"
      >
      <div class="absolute top-2 left-2 flex items-center gap-1.5 bg-red-600 px-2 py-0.5 rounded-md">
        <span class="size-1.5 rounded-full bg-white animate-pulse" />
        <span class="text-[10px] font-bold text-white uppercase tracking-wider">Live</span>
      </div>
      <div class="absolute top-2 right-2 flex items-center gap-1.5">
        <span
          v-if="streamUptime"
          class="bg-black/60 px-1.5 py-0.5 rounded text-[10px] text-white/70"
        >
          {{ streamUptime }}
        </span>
        <span class="bg-black/60 px-1.5 py-0.5 rounded text-[10px] text-white/90">
          {{ formatCount(profileData.viewerCount) }} viewers
        </span>
      </div>
    </div>
    <div
      v-else-if="tw.showBanner && profileData.offlineImageUrl"
      class="w-full h-20 overflow-hidden relative"
    >
      <img
        :src="profileData.offlineImageUrl"
        :alt="`${profileData.displayName} offline banner`"
        class="w-full h-full object-cover"
        loading="lazy"
      >
      <!-- Offline badge on banner -->
      <div
        v-if="tw.showLiveStatus"
        class="absolute top-2 left-2 flex items-center gap-1.5 bg-white/10 backdrop-blur-sm px-2 py-0.5 rounded-md"
      >
        <span class="size-1.5 rounded-full bg-white/40" />
        <span class="text-[10px] font-medium text-white/60 uppercase tracking-wider">Offline</span>
      </div>
    </div>

    <!-- Channel header -->
    <a
      :href="profileData.channelUrl"
      target="_blank"
      rel="noopener noreferrer"
      class="flex items-center gap-2.5 px-3 pt-3 pb-2 no-underline group/header"
    >
      <div class="relative shrink-0">
        <img
          v-if="profileData.avatarUrl"
          :src="profileData.avatarUrl"
          :alt="profileData.displayName"
          class="size-10 rounded-full object-cover"
          :class="profileData.isLive && tw.showLiveStatus ? 'ring-2 ring-red-500' : ''"
        >
        <!-- Offline dot on avatar when no banner shown -->
        <span
          v-if="tw.showLiveStatus && !profileData.isLive && !(tw.showBanner && profileData.offlineImageUrl)"
          class="absolute -bottom-0.5 -right-0.5 size-3 rounded-full bg-gray-500 border-2 border-black/40"
        />
      </div>
      <div
        v-if="!profileData.avatarUrl"
        class="relative shrink-0"
      >
        <div class="size-10 rounded-full bg-[#9146FF]/20 flex items-center justify-center">
          <UIcon
            name="i-simple-icons-twitch"
            class="size-4 text-[#9146FF]"
          />
        </div>
        <span
          v-if="tw.showLiveStatus && !profileData.isLive && !(tw.showBanner && profileData.offlineImageUrl)"
          class="absolute -bottom-0.5 -right-0.5 size-3 rounded-full bg-gray-500 border-2 border-black/40"
        />
      </div>
      <div class="min-w-0 flex-1">
        <div class="flex items-center gap-1.5">
          <span class="text-sm font-semibold text-white leading-tight truncate group-hover/header:text-[#9146FF] transition-colors">
            {{ profileData.displayName }}
          </span>
          <span
            v-if="broadcasterBadge"
            class="text-[9px] font-medium px-1.5 py-0.5 rounded bg-[#9146FF]/15 text-[#9146FF] shrink-0"
          >
            {{ broadcasterBadge }}
          </span>
        </div>
        <span class="text-xs text-white/40 leading-tight block">
          {{ formatCount(profileData.followerCount) }} followers
          <template v-if="tw.showSubscriberCount && profileData.subscriberCount !== null">
            <span class="text-white/20 mx-0.5">&middot;</span>
            {{ formatCount(profileData.subscriberCount) }} subs
          </template>
        </span>
      </div>
    </a>

    <!-- Currently playing / last stream info -->
    <div
      v-if="profileData.gameName || profileData.title"
      class="px-3 pb-2"
    >
      <div
        v-if="profileData.gameName"
        class="text-[11px] text-white/50"
      >
        {{ profileData.isLive ? 'Playing' : 'Last played' }}: <span class="text-white/70">{{ profileData.gameName }}</span>
      </div>
      <div
        v-if="profileData.title"
        class="text-[11px] text-white/30 line-clamp-1 mt-0.5"
      >
        {{ profileData.title }}
      </div>
    </div>

    <!-- Channel description -->
    <div
      v-if="tw.showDescription && profileData.description"
      class="px-3 pb-2"
    >
      <p class="text-[11px] text-white/40 line-clamp-2">
        {{ profileData.description }}
      </p>
    </div>

    <!-- Stream tags -->
    <div
      v-if="tw.showTags && profileData.tags.length > 0"
      class="px-3 pb-2 flex flex-wrap gap-1"
    >
      <span
        v-for="tag in profileData.tags.slice(0, 5)"
        :key="tag"
        class="text-[10px] px-1.5 py-0.5 bg-white/5 text-white/50 rounded"
      >
        {{ tag }}
      </span>
    </div>

    <!-- Schedule -->
    <div
      v-if="tw.showSchedule && profileData.schedule && profileData.schedule.length > 0"
      class="px-3 pb-2"
    >
      <div class="text-[10px] font-medium text-white/30 uppercase tracking-wider mb-1.5">
        Upcoming
      </div>
      <div class="space-y-1.5">
        <div
          v-for="segment in profileData.schedule"
          :key="segment.id"
          class="flex items-start gap-2 text-[11px]"
        >
          <UIcon
            name="i-lucide-calendar"
            class="size-3 text-[#9146FF]/60 shrink-0 mt-0.5"
          />
          <div class="min-w-0">
            <span class="text-white/60 block leading-tight truncate">{{ segment.title }}</span>
            <span class="text-white/30">
              {{ formatScheduleTime(segment.startTime) }}
              <template v-if="segment.category">
                &middot; {{ segment.category }}
              </template>
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- Clips: grid (<=3) -->
    <div
      v-if="visibleClips.length > 0 && !useCarousel"
      class="px-3 pb-2"
    >
      <div class="grid grid-cols-3 gap-2">
        <a
          v-for="clip in visibleClips"
          :key="clip.id"
          :href="clip.url"
          target="_blank"
          rel="noopener noreferrer"
          class="group/clip no-underline"
        >
          <div class="relative aspect-video rounded-lg overflow-hidden bg-white/5">
            <img
              :src="clip.thumbnailUrl"
              :alt="clip.title"
              class="w-full h-full object-cover group-hover/clip:scale-105 transition-transform duration-200"
              loading="lazy"
            >
            <div class="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover/clip:opacity-100 transition-opacity duration-200">
              <UIcon
                name="i-lucide-external-link"
                class="size-4 text-white"
              />
            </div>
            <div class="absolute bottom-1 right-1 bg-black/70 px-1 py-0.5 rounded text-[9px] text-white/80">
              {{ formatDuration(clip.duration) }}
            </div>
          </div>
          <p class="text-[11px] text-white/60 leading-tight mt-1 line-clamp-2 h-[2.4em] group-hover/clip:text-white/80 transition-colors">
            {{ clip.title }}
          </p>
          <span class="text-[10px] text-white/30">{{ formatCount(clip.viewCount) }} views</span>
        </a>
      </div>
    </div>

    <!-- Clips: carousel (>3) -->
    <div
      v-else-if="visibleClips.length > 0 && useCarousel"
      class="px-3 pb-2 overflow-hidden"
    >
      <UCarousel
        v-slot="{ item }"
        :items="carouselItems"
        arrows
        :ui="{ prev: 'start-2 sm:start-2 top-1/3', next: 'end-2 sm:end-2 top-1/3' }"
        :prev="{ onClick: onClickPrev, size: 'xs' }"
        :next="{ onClick: onClickNext, size: 'xs' }"
        @select="onSelect"
      >
        <a
          :href="(item as unknown as TwitchClip).url"
          target="_blank"
          rel="noopener noreferrer"
          class="group/clip no-underline"
        >
          <div class="relative aspect-video rounded-lg overflow-hidden bg-white/5">
            <img
              :src="(item as unknown as TwitchClip).thumbnailUrl"
              :alt="(item as unknown as TwitchClip).title"
              class="w-full h-full object-cover group-hover/clip:scale-105 transition-transform duration-200"
              loading="lazy"
            >
            <div class="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 group-hover/clip:opacity-100 transition-opacity duration-200">
              <UIcon
                name="i-lucide-external-link"
                class="size-4 text-white"
              />
            </div>
            <div class="absolute bottom-1 right-1 bg-black/70 px-1 py-0.5 rounded text-[9px] text-white/80">
              {{ formatDuration((item as unknown as TwitchClip).duration) }}
            </div>
          </div>
          <p class="text-[11px] text-white/60 leading-tight mt-1 line-clamp-2 h-[2.4em] group-hover/clip:text-white/80 transition-colors">
            {{ (item as unknown as TwitchClip).title }}
          </p>
          <span class="text-[10px] text-white/30">{{ formatCount((item as unknown as TwitchClip).viewCount) }} views</span>
        </a>
      </UCarousel>
    </div>

    <!-- Footer with Twitch branding -->
    <div class="flex items-center justify-end px-3 pb-2.5">
      <UIcon
        name="i-simple-icons-twitch"
        class="size-3 text-white/15 group-hover:text-[#9146FF] transition-colors"
      />
    </div>

    <!-- Manage button (edit mode) -->
    <button
      v-if="editMode"
      class="w-full flex items-center justify-center gap-2 py-2 px-4 border-t border-white/6 transition-all duration-200 hover:bg-white/4 cursor-pointer"
      :style="{ color: 'rgba(255, 255, 255, 0.4)' }"
      @click="emit('open-twitch-settings')"
    >
      <UIcon
        name="i-lucide-settings"
        class="size-4"
      />
      <span class="text-sm">Manage embed</span>
    </button>
  </div>
</template>
